import { supabase } from './supabase';

export type MediBlogArticleStatus = 'draft' | 'published' | 'archived';

export interface MediBlogCategory {
  id?: string;
  name_en: string;
  name_ar?: string;
  name_fr?: string;
  slug: string;
  description?: string;
  is_active?: boolean;
  display_order?: number;
  created_at?: string;
  updated_at?: string;
}

export interface MediBlogArticle {
  id?: string;
  category_id?: string | null;
  slug: string;
  title_en: string;
  title_ar?: string;
  title_fr?: string;
  excerpt_en?: string;
  excerpt_ar?: string;
  excerpt_fr?: string;
  content_en: string;
  content_ar?: string;
  content_fr?: string;
  featured_image?: string;
  author_name?: string;
  status?: MediBlogArticleStatus;
  published_at?: string | null;
  meta_title?: string;
  meta_description?: string;
  meta_keywords?: string;
  og_title?: string;
  og_description?: string;
  og_image?: string;
  canonical_url?: string;
  no_index?: boolean;
  display_order?: number;
  created_at?: string;
  updated_at?: string;
  category?: MediBlogCategory | null;
}

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

class MediBlogServiceClass {
  private isMissingRelationError(error: unknown): boolean {
    if (!error || typeof error !== 'object') return false;
    const code = 'code' in error && typeof error.code === 'string' ? error.code : '';
    const status = 'status' in error && typeof error.status === 'number' ? error.status : 0;
    const message = 'message' in error && typeof error.message === 'string' ? error.message : '';
    return (
      code === 'PGRST205' ||
      code === '42P01' ||
      status === 404 ||
      status === 406 ||
      message.toLowerCase().includes('could not find the table')
    );
  }

  // --- Categories ---

  async getCategories(activeOnly = false) {
    try {
      let query = supabase.from('mediblog_categories').select('*');
      if (activeOnly) query = query.eq('is_active', true);
      query = query.order('display_order', { ascending: true });
      const { data, error } = await query;
      if (error) {
        if (this.isMissingRelationError(error)) return { data: [] as MediBlogCategory[], error: null };
        throw error;
      }
      return { data: (data ?? []) as MediBlogCategory[], error: null };
    } catch (error) {
      if (!this.isMissingRelationError(error)) {
        console.error('Error fetching MediBlog categories:', error);
      }
      return { data: [] as MediBlogCategory[], error: null };
    }
  }

  async createCategory(category: MediBlogCategory) {
    const { data, error } = await supabase
      .from('mediblog_categories')
      .insert(category)
      .select()
      .single();
    if (error) throw error;
    return { data: data as MediBlogCategory, error: null };
  }

  async updateCategory(id: string, category: Partial<MediBlogCategory>) {
    const { data, error } = await supabase
      .from('mediblog_categories')
      .update(category)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return { data: data as MediBlogCategory, error: null };
  }

  async deleteCategory(id: string) {
    const { error } = await supabase.from('mediblog_categories').delete().eq('id', id);
    if (error) throw error;
    return { error: null };
  }

  // --- Articles ---

  async getArticles(options: { publishedOnly?: boolean; categorySlug?: string } = {}) {
    try {
      let query = supabase
        .from('mediblog_articles')
        .select('*, category:mediblog_categories(*)');

      if (options.publishedOnly) {
        query = query.eq('status', 'published');
      }

      if (options.categorySlug) {
        const { data: cat } = await supabase
          .from('mediblog_categories')
          .select('id')
          .eq('slug', options.categorySlug)
          .maybeSingle();
        if (cat?.id) {
          query = query.eq('category_id', cat.id);
        }
      }

      query = query
        .order('published_at', { ascending: false, nullsFirst: false })
        .order('display_order', { ascending: true });

      const { data, error } = await query;
      if (error) {
        if (this.isMissingRelationError(error)) return { data: [] as MediBlogArticle[], error: null };
        throw error;
      }
      return { data: (data ?? []) as MediBlogArticle[], error: null };
    } catch (error) {
      if (!this.isMissingRelationError(error)) {
        console.error('Error fetching MediBlog articles:', error);
      }
      return { data: [] as MediBlogArticle[], error: null };
    }
  }

  async getArticleBySlug(slug: string, publishedOnly = true) {
    try {
      let query = supabase
        .from('mediblog_articles')
        .select('*, category:mediblog_categories(*)')
        .eq('slug', slug);

      if (publishedOnly) {
        query = query.eq('status', 'published');
      }

      const { data, error } = await query.maybeSingle();
      if (error) {
        if (this.isMissingRelationError(error)) return { data: null, error: null };
        throw error;
      }
      return { data: data as MediBlogArticle | null, error: null };
    } catch (error) {
      console.error('Error fetching MediBlog article:', error);
      return { data: null, error };
    }
  }

  async createArticle(article: MediBlogArticle) {
    const payload = { ...article };
    if (payload.status === 'published' && !payload.published_at) {
      payload.published_at = new Date().toISOString();
    }
    const { data, error } = await supabase
      .from('mediblog_articles')
      .insert(payload)
      .select('*, category:mediblog_categories(*)')
      .single();
    if (error) throw error;
    return { data: data as MediBlogArticle, error: null };
  }

  async updateArticle(id: string, article: Partial<MediBlogArticle>) {
    const payload = { ...article };
    if (payload.status === 'published' && !payload.published_at) {
      payload.published_at = new Date().toISOString();
    }
    const { data, error } = await supabase
      .from('mediblog_articles')
      .update(payload)
      .eq('id', id)
      .select('*, category:mediblog_categories(*)')
      .single();
    if (error) throw error;
    return { data: data as MediBlogArticle, error: null };
  }

  async deleteArticle(id: string) {
    const { error } = await supabase.from('mediblog_articles').delete().eq('id', id);
    if (error) throw error;
    return { error: null };
  }
}

export const mediBlogService = new MediBlogServiceClass();
